import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { tableIcons } from '../../constants/assets'
import { cn } from '../../utils/cn'

export type RowAction = {
  id: string
  label: string
  onSelect: () => void
  danger?: boolean
  icon?: React.ElementType
}

type RowActionsMenuProps = {
  /** Names the row the menu belongs to, e.g. "SCB-10254 — John Smith". */
  subject: string
  actions: RowAction[]
}

type MenuPosition = {
  placement: 'above' | 'below'
  offset: number
  right: number
  maxHeight: number
}

/**
 * Shared three-dot action menu.
 *
 * The menu is rendered through a portal so table overflow cannot
 * clip it or create an additional scrollbar.
 */
export const RowActionsMenu = ({
  subject,
  actions,
}: RowActionsMenuProps) => {
  const [open, setOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  const [menuPosition, setMenuPosition] =
    useState<MenuPosition>({
      placement: 'below',
      offset: 0,
      right: 12,
      maxHeight: 360,
    })

  const updateMenuPosition = useCallback(() => {
    const trigger = buttonRef.current

    if (!trigger) return

    const rect = trigger.getBoundingClientRect()

    const viewportPadding = 12
    const menuGap = 6

    /*
     * Approximate the natural menu height.
     * Each action is approximately 44px tall.
     */
    const expectedMenuHeight = Math.min(
      actions.length * 44 + 8,
      360,
    )

    const spaceBelow =
      window.innerHeight -
      rect.bottom -
      menuGap -
      viewportPadding

    const spaceAbove =
      rect.top -
      menuGap -
      viewportPadding

    /*
     * Normal rows open below.
     * Rows near the viewport bottom open above.
     */
    const placement: MenuPosition['placement'] =
      spaceBelow >= expectedMenuHeight ||
      spaceBelow >= spaceAbove
        ? 'below'
        : 'above'

    const availableHeight =
      placement === 'below'
        ? spaceBelow
        : spaceAbove

    setMenuPosition({
      placement,

      offset:
        placement === 'below'
          ? rect.bottom + menuGap
          : window.innerHeight - rect.top + menuGap,

      right: Math.max(
        viewportPadding,
        window.innerWidth - rect.right,
      ),

      maxHeight: Math.max(
        120,
        Math.min(360, availableHeight),
      ),
    })
  }, [actions.length])

  const handleToggle = () => {
    if (!open) {
      updateMenuPosition()
    }

    setOpen((current) => !current)
  }

  /*
   * The menu is rendered outside containerRef through a portal.
   * Therefore both the trigger and portal menu must be checked.
   */
  useEffect(() => {
    if (!open) return

    const handlePointerDown = (
      event: PointerEvent,
    ) => {
      const target = event.target as Node

      const clickedTrigger =
        containerRef.current?.contains(target)

      const clickedMenu =
        menuRef.current?.contains(target)

      if (!clickedTrigger && !clickedMenu) {
        setOpen(false)
      }
    }

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
    )

    document.addEventListener(
      'keydown',
      handleKeyDown,
    )

    return () => {
      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      )

      document.removeEventListener(
        'keydown',
        handleKeyDown,
      )
    }
  }, [open])

  /*
   * Keep the portal aligned with the three-dot button when
   * the dashboard content scrolls or the window resizes.
   */
  useEffect(() => {
    if (!open) return

    updateMenuPosition()

    window.addEventListener(
      'resize',
      updateMenuPosition,
    )

    window.addEventListener(
      'scroll',
      updateMenuPosition,
      true,
    )

    return () => {
      window.removeEventListener(
        'resize',
        updateMenuPosition,
      )

      window.removeEventListener(
        'scroll',
        updateMenuPosition,
        true,
      )
    }
  }, [open, updateMenuPosition])

  return (
    <div
      ref={containerRef}
      className="relative flex justify-center"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Actions for ${subject}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleToggle}
        className="block h-[26px] w-[28px] shrink-0"
      >
        <img
          alt=""
          aria-hidden
          className="block size-full max-w-none"
          src={tableIcons.rowActions}
        />
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <ul
            ref={menuRef}
            role="menu"
            style={{
              right: menuPosition.right,
              maxHeight: menuPosition.maxHeight,

              ...(menuPosition.placement === 'below'
                ? { top: menuPosition.offset }
                : { bottom: menuPosition.offset }),
            }}
            className="fixed z-[1000] flex w-max min-w-[150px] max-w-[240px] flex-col overflow-x-hidden overflow-y-auto overscroll-contain rounded-[12px] border border-solid border-dash-field bg-white py-[4px] shadow-[0_12px_32px_rgba(15,23,42,0.18)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {actions.map((action) => (
              <li key={action.id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    action.onSelect()
                    setOpen(false)
                  }}
                  className={cn(
                    'flex w-full items-center gap-[12px] whitespace-nowrap px-[16px] py-[12px] text-left font-host text-[14px] font-medium leading-normal text-black transition-colors hover:bg-dash-row-head-bg',
                    action.danger
                      ? 'text-black'
                      : 'text-black',
                  )}
                >
                  {action.icon && (
                    <action.icon
                      size={16}
                      className="shrink-0 text-black"
                    />
                  )}

                  <span className="text-black">
                    {action.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  )
}